import React from 'react';
import { useTranslation } from 'localization';
import { ScopeMode, SphereSubscriptionWithChannel, EffectiveSubscription } from 'types/channel';
import ChannelSubscriptionCardMolecule from './ChannelSubscriptionCardMolecule';
import './ChannelSubscriptionListMolecule.scss';

export interface ChannelSubscriptionListMoleculeProps {
  effectiveSubscriptions: EffectiveSubscription[];
  directSubscriptions: SphereSubscriptionWithChannel[];
  onUnsubscribe?: (subscriptionId: string) => void;
  onScopeChange?: (subscriptionId: string, scopeMode: ScopeMode) => void;
}

const ChannelSubscriptionListMolecule: React.FC<ChannelSubscriptionListMoleculeProps> = ({
  effectiveSubscriptions,
  directSubscriptions,
  onUnsubscribe,
  onScopeChange,
}) => {
  const { t } = useTranslation();

  const directChannelSystemNames = new Set(directSubscriptions.map((s) => s.channel_system_name));
  const inheritedSubscriptions = effectiveSubscriptions.filter(
    (es) => es.source === 'inherited' && !directChannelSystemNames.has(es.channelSystemName)
  );

  const hasNoSubscriptions = directSubscriptions.length === 0 && inheritedSubscriptions.length === 0;

  if (hasNoSubscriptions) {
    return (
      <div className="marketplace-channel-subscription-list-molecule">
        <p className="marketplace-channel-subscription-list-molecule__empty">
          {t('sphereChannels.list.emptyAll')}
        </p>
      </div>
    );
  }

  return (
    <div className="marketplace-channel-subscription-list-molecule">
      <section className="marketplace-channel-subscription-list-molecule__section">
        <h4 className="marketplace-channel-subscription-list-molecule__section-title">
          {t('sphereChannels.list.directTitle')}
        </h4>
        {directSubscriptions.length === 0 ? (
          <p className="marketplace-channel-subscription-list-molecule__empty">
            {t('sphereChannels.list.emptyDirect')}
          </p>
        ) : (
          <div className="marketplace-channel-subscription-list-molecule__cards">
            {directSubscriptions.map((sub) => (
              <ChannelSubscriptionCardMolecule
                key={sub.id}
                subscription={{
                  id: sub.id,
                  channel: sub.channel,
                  scope_mode: sub.scope_mode,
                }}
                isInherited={false}
                onUnsubscribe={onUnsubscribe}
                onScopeChange={onScopeChange}
              />
            ))}
          </div>
        )}
      </section>

      <section className="marketplace-channel-subscription-list-molecule__section">
        <h4 className="marketplace-channel-subscription-list-molecule__section-title">
          {t('sphereChannels.list.inheritedTitle')}
        </h4>
        {inheritedSubscriptions.length === 0 ? (
          <p className="marketplace-channel-subscription-list-molecule__empty">
            {t('sphereChannels.list.emptyInherited')}
          </p>
        ) : (
          <div className="marketplace-channel-subscription-list-molecule__cards">
            {inheritedSubscriptions.map((es) => (
              <ChannelSubscriptionCardMolecule
                key={es.channelSystemName}
                subscription={{
                  id: es.channel.id,
                  channel: es.channel,
                }}
                isInherited={true}
                sourceSphereName={es.sourceSphereName}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default ChannelSubscriptionListMolecule;
